'use client';
import { PieChart, Pie, ResponsiveContainer, Tooltip } from 'recharts';
import { ChartCard } from '../../Cards/ChartCards/ChartCard';
import { CustomTooltip } from '../shared/CustomTooltip/CustomTooltip';
import styles from './PiePercentageChart.module.css';
import type { DonutChartData } from '../DonutChart/DonutChart';
import { PIE_CONFIG, usePieChart } from '@practium/features/dashboard';
import { PieLegend } from './PieLegend';
import { PieSlice } from './PieSlice';
import { SkeletonBox } from '@practium/ui';

export interface PieChartProps {
	title: string;
	subtitle: string;
	buttonText: string;
	onButtonClick: () => void;
	data: DonutChartData[];
	emptyMessage?: string;
	isLoading?: boolean;
}

const PieSkeleton = () => (
	<>
		<div className={`${styles.chartContainer} ${styles.skeletonChart}`}>
			<SkeletonBox width="11rem" height="11rem" borderRadius="50%" />
		</div>
		<div className={styles.legend}>
			{Array.from({ length: 2 }, (_, index) => (
				<div key={index} className={styles.legendItem}>
					<SkeletonBox width="0.75rem" height="0.75rem" borderRadius="50%" />
					<SkeletonBox width="5rem" height="2rem" />
				</div>
			))}
		</div>
	</>
);

export const PiePercentageChart = ({
	title,
	subtitle,
	buttonText,
	onButtonClick,
	data,
	emptyMessage,
	isLoading,
}: PieChartProps) => {
	const isEmpty = !data || data.length === 0;
	const { enrichedData, total, handleItemHover } = usePieChart(data);

	return (
		<ChartCard
			title={title}
			subtitle={subtitle}
			buttonText={buttonText}
			onButtonClick={onButtonClick}
			isEmpty={isEmpty}
			emptyMessage={emptyMessage}
			isLoading={isLoading}
			skeleton={<PieSkeleton />}
		>
			<div className={styles.chartContainer}>
				<ResponsiveContainer width="100%" height="100%">
					<PieChart>
						<Pie
							data={enrichedData as any}
							cx="50%"
							cy="50%"
							labelLine={false}
							outerRadius={PIE_CONFIG.outerRadius}
							innerRadius={PIE_CONFIG.innerRadius}
							dataKey="value"
							startAngle={PIE_CONFIG.startAngle}
							endAngle={PIE_CONFIG.endAngle}
							stroke="none"
							onMouseEnter={(_, index) => handleItemHover(index)}
							onMouseLeave={() => handleItemHover(undefined)}
							isAnimationActive={false}
							style={{ minHeight: '20rem' }}
						>
							{enrichedData.map((item) => (
								<PieSlice key={item.name} fill={item.fill} />
							))}
						</Pie>
						<Tooltip
							content={<CustomTooltip subtitle={subtitle} variant="donut" />}
							cursor={false}
							allowEscapeViewBox={{ x: true, y: true }}
							wrapperStyle={{ outline: 'none', zIndex: 1000 }}
						/>
					</PieChart>
				</ResponsiveContainer>
			</div>

			<PieLegend items={enrichedData} total={total} />
		</ChartCard>
	);
};
